import React, { useCallback, useMemo, useEffect, useRef } from "react";
import { GoogleMap, useJsApiLoader, Marker, Polyline, Polygon, InfoWindow } from "@react-google-maps/api";

const containerDefault = { width: "100%", height: "100%" };
const LIBRARIES = ["places"];

export default function RideMap({
  center = { lat: 46.2044, lng: 6.1432 },
  pickup,
  destination,
  drivers = [],
  onMapClick,
  route,
  routes = [],
  selectedRouteKind = null,
  height = "500px",
  sectors = [],
  demand = {},
}) {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES,
  });

  const mapCenter = useMemo(() => {
    if (Array.isArray(center)) return { lat: center[0], lng: center[1] };
    if (pickup) return { lat: pickup.lat, lng: pickup.lng };
    return center || { lat: 46.2044, lng: 6.1432 };
  }, [center, pickup]);

  const mapRef = useRef(null);
  const onLoad = useCallback((m) => { mapRef.current = m; }, []);
  const handleClick = useCallback((e) => {
    if (onMapClick) onMapClick({ lat: e.latLng.lat(), lng: e.latLng.lng() });
  }, [onMapClick]);

  useEffect(() => {
    if (!mapRef.current || !pickup) return;
    mapRef.current.panTo({ lat: pickup.lat, lng: pickup.lng });
  }, [pickup]);

  const maxDemand = Math.max(1, ...Object.values(demand || {}));

  const polyPath = (latlngPairs) => (latlngPairs || []).map(([lat, lng]) => ({ lat, lng }));

  if (!isLoaded) {
    return (
      <div style={{ height }} className="border-2 border-black shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] flex items-center justify-center bg-[#F4F4F5]">
        <p className="font-mono-eyebrow">// Chargement Google Maps...</p>
      </div>
    );
  }

  return (
    <div style={{ height }} className="border-2 border-black shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] overflow-hidden">
      <GoogleMap
        mapContainerStyle={containerDefault}
        center={mapCenter}
        zoom={13}
        onLoad={onLoad}
        onClick={handleClick}
        options={{
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
        }}
      >
        {sectors.map((s) => {
          const c = (demand && demand[s.id]) || 0;
          const t = c / maxDemand;
          const fill = c === 0 ? "#FFD600" : "#FF3B30";
          return (
            <Polygon
              key={s.id}
              paths={polyPath(s.polygon)}
              options={{
                fillColor: fill,
                fillOpacity: c === 0 ? 0.06 : 0.18 + t * 0.45,
                strokeColor: "#0A0A0A",
                strokeWeight: 1.5,
                strokeOpacity: 0.6,
              }}
            />
          );
        })}

        {pickup && (
          <Marker position={{ lat: pickup.lat, lng: pickup.lng }} label={{ text: "A", color: "#FFD600", fontWeight: "bold" }} />
        )}
        {destination && (
          <Marker position={{ lat: destination.lat, lng: destination.lng }} label={{ text: "B", color: "#0A0A0A", fontWeight: "bold" }} />
        )}
        {drivers.map((d) => d.current_lat && d.current_lng ? (
          <Marker key={d.user_id}
                  position={{ lat: d.current_lat, lng: d.current_lng }}
                  label={{ text: "D", color: "#0A0A0A", fontWeight: "bold" }} />
        ) : null)}

        {route && route.length > 1 && (
          <Polyline path={polyPath(route)} options={{ strokeColor: "#0A0A0A", strokeOpacity: 1, strokeWeight: 5 }} />
        )}
        {routes.map((r) => {
          const isSelected = r.kind === selectedRouteKind;
          const color = r.kind === "fast" ? "#FFD600" : "#3B82F6";
          return (
            <React.Fragment key={r.kind}>
              <Polyline
                path={polyPath(r.polyline)}
                options={{
                  strokeColor: color,
                  strokeOpacity: isSelected ? 0.7 : 0.35,
                  strokeWeight: isSelected ? 14 : 9,
                }}
              />
              <Polyline
                path={polyPath(r.polyline)}
                options={{
                  strokeColor: "#0A0A0A",
                  strokeOpacity: isSelected ? 1 : 0.55,
                  strokeWeight: isSelected ? 5 : 3,
                }}
              />
            </React.Fragment>
          );
        })}
      </GoogleMap>
    </div>
  );
}
